import { inject, Injectable } from '@angular/core';
import { LoggerService } from '@lx/shared/logger';
import { DOCUMENT } from '@angular/common';

/**
 * Service to initialize Google Tag Manager with container ID from configuration
 * The GTM container ID is fetched from the QUOTE_TOOL_GTM_CONTAINER_ID configuration property
 */
@Injectable({
  providedIn: 'root',
})
export class GtmInitializationService {
  private readonly document = inject(DOCUMENT);
  private readonly logger = inject(LoggerService);

  /**
   * GTM container ID format validation pattern
   * GTM IDs follow the format: GTM-XXXXXX... (where X is alphanumeric, minimum 6 characters)
   * Examples: GTM-XXXXXX, GTM-TJQB768, GTM-T7R98LC
   * @private
   */
  private readonly GTM_ID_PATTERN = /^GTM-[A-Z0-9]{6,}$/;

  /**
   * Marker ID used to identify GTM script tags and prevent duplicate injection
   * @private
   */
  private readonly GTM_SCRIPT_ID = 'gtm-initialization-script';

  /**
   * Marker ID used to identify GTM noscript fallback
   * @private
   */
  private readonly GTM_NOSCRIPT_ID = 'gtm-initialization-noscript';

  /**
   * Initializes Google Tag Manager with the provided container ID
   * Idempotent: safe to call multiple times, will not inject duplicate tags
   * @param containerId - The GTM container ID to initialize, or undefined if not available
   * @returns void
   */
  initialize(containerId: string | undefined): void {
    if (!containerId || typeof containerId !== 'string') {
      this.logger.warn('Configuration not available for GTM initialization or value is not valid.');
      return;
    }

    if (!this.isValidGtmId(containerId)) {
      this.logger.error(`Invalid GTM container ID format: ${containerId}. Expected format: GTM-XXXXXX (6+ alphanumeric characters)`);
      return;
    }

    // Check if GTM is already initialized to prevent duplicate injection
    if (this.isAlreadyInitialized()) {
      this.logger.warn('GTM is already initialized. Skipping duplicate initialization.');
      return;
    }

    this.injectGtmScript(containerId);
    this.injectGtmNoscript(containerId);
  }

  /**
   * Checks if GTM has already been initialized by looking for marker scripts in the DOM
   * @returns true if GTM scripts are already present, false otherwise
   * @private
   */
  private isAlreadyInitialized(): boolean {
    const existingGtmScript = this.document.getElementById(this.GTM_SCRIPT_ID) !== null;
    const existingGtmNoscript = this.document.getElementById(this.GTM_NOSCRIPT_ID) !== null;
    return existingGtmScript || existingGtmNoscript;
  }

  /**
   * Validates that the container ID matches the expected GTM format
   * @param containerId - The container ID to validate
   * @returns true if the ID matches the GTM pattern, false otherwise
   * @private
   */
  private isValidGtmId(containerId: string): boolean {
    return this.GTM_ID_PATTERN.test(containerId);
  }

  /**
   * Injects the GTM script tag into the document head
   * Uses the standard gtm.js loader with gtm.start event bootstrap
   * Important: dataLayer must be initialized BEFORE loading gtm.js to prevent race conditions
   * @param containerId - The GTM container ID (GTM-XXXXX format)
   * @private
   */
  private injectGtmScript(containerId: string): void {
    // Step 1: Initialize dataLayer and push gtm.start event (must happen first)
    const gtmBootstrapScript = this.document.createElement('script');
    gtmBootstrapScript.id = this.GTM_SCRIPT_ID;
    gtmBootstrapScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
    `;
    this.document.head.appendChild(gtmBootstrapScript);

    // Step 2: Load external GTM script (after dataLayer is ready)
    const gtmLoaderScript = this.document.createElement('script');
    gtmLoaderScript.async = true;
    gtmLoaderScript.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    this.document.head.appendChild(gtmLoaderScript);
  }

  /**
   * Injects the GTM noscript fallback into the document body
   * Provides tracking fallback for users with JavaScript disabled
   * Uses safe DOM creation (createElement) instead of innerHTML to prevent XSS
   * Safe because containerId is validated against GTM ID pattern before injection
   * @param containerId - The GTM container ID (GTM-XXXXX format)
   * @private
   */
  private injectGtmNoscript(containerId: string): void {
    const noscript = this.document.createElement('noscript');
    noscript.id = this.GTM_NOSCRIPT_ID;
    const iframe = this.document.createElement('iframe');

    // Set iframe attributes safely - no HTML string interpolation
    iframe.setAttribute('src', `https://www.googletagmanager.com/ns.html?id=${containerId}`);
    iframe.setAttribute('height', '0');
    iframe.setAttribute('width', '0');
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    // Append iframe to noscript element
    noscript.appendChild(iframe);

    // Insert noscript as first element in body
    this.document.body.insertBefore(noscript, this.document.body.firstChild);
  }
}
